// 美国合规（地理围栏 + KYC）运行时状态

import { createWithEqualityFn } from "zustand/traditional";
import { verifyGeoPayload } from "../api/geo";
import { getUsKycStatus } from "../api/kyc";
import { requestBrowserGeolocation } from "../geolocation";
import { KYC_ID_STATUS } from "../types";
import type { GeoStatus, UsKycStatus } from "../types";

/**
 * 取定位的在飞请求。并发调用必须共用同一次：浏览器对并发的 getCurrentPosition
 * 会各弹各的权限框，用户会看到框闪两下。
 */
let geoCheckInFlight: Promise<GeoStatus> | null = null;

interface ComplianceState {
  /** 地理围栏判定结果，语义见 types.ts */
  geoStatus: GeoStatus;
  /** 取不到定位时是「用户禁止」还是「设备/信号问题」，决定弹窗文案 */
  geoDenied: boolean;
  kycStatus: UsKycStatus | null;
  /** 两个拦截弹窗的开合。
   *  用 store 而不是 window 事件：弹窗组件是 lazy 的，事件派发时它可能还没挂载，
   *  而 store 里的 true 会一直留着，组件挂上来自然就显示，不需要 DepositPopup
   *  那套「迟到认领」兜底。 */
  geoDialogVisible: boolean;
  kycDialogVisible: boolean;
}

interface ComplianceActions {
  /**
   * 取一次定位并上报校验。返回最终状态，调用方可直接据此决定拦不拦。
   * fromUserRetry：是否来自地理围栏弹窗里那次主动重试，只影响临时兜底
   * （见 geolocation.ts 末尾），自动检查一律传 false。
   */
  runGeoCheck: (fromUserRetry?: boolean) => Promise<GeoStatus>;
  refreshKycStatus: () => Promise<UsKycStatus>;
  setGeoDialogVisible: (visible: boolean) => void;
  setKycDialogVisible: (visible: boolean) => void;
  resetCompliance: () => void;
}

const initialState: ComplianceState = {
  geoStatus: "unknown",
  geoDenied: false,
  kycStatus: null,
  geoDialogVisible: false,
  kycDialogVisible: false,
};

export const useComplianceStore = createWithEqualityFn<
  ComplianceState & ComplianceActions
>()(set => ({
  ...initialState,

  runGeoCheck: (fromUserRetry = false) => {
    if (geoCheckInFlight) return geoCheckInFlight;

    set({ geoStatus: "checking" });
    geoCheckInFlight = (async (): Promise<GeoStatus> => {
      const located = await requestBrowserGeolocation(fromUserRetry);
      if (!located.ok) {
        set({ geoStatus: "denied", geoDenied: located.reason === "denied" });
        return "denied";
      }
      const result = await verifyGeoPayload(located.payload);
      const status: GeoStatus = result.compliant ? "compliant" : "illegal";
      set({ geoStatus: status, geoDenied: false });
      return status;
    })().finally(() => {
      geoCheckInFlight = null;
    });

    return geoCheckInFlight;
  },

  refreshKycStatus: async () => {
    try {
      const status = await getUsKycStatus();
      set({ kycStatus: status });
      return status;
    } catch {
      // fail-open：查不到当作已通过，理由见 api/kyc.ts 顶部注释
      const fallback: UsKycStatus = {
        kycAge: 1,
        kycId: KYC_ID_STATUS.APPROVED,
        kycPoa: 1,
      };
      set({ kycStatus: fallback });
      return fallback;
    }
  },

  setGeoDialogVisible: visible => set({ geoDialogVisible: visible }),
  setKycDialogVisible: visible => set({ kycDialogVisible: visible }),

  resetCompliance: () => {
    geoCheckInFlight = null;
    set({ ...initialState });
  },
}));

/** 非 React 场景（门禁函数）读状态用。 */
export const getComplianceState = () => useComplianceStore.getState();
